import { useState } from 'react';
import { T, useGT } from 'gt-react';
import type { ItemListDTO } from '@erp/rpc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useStageFromItemList } from '@/api/rpc/hooks/inventory-management/useMetadataChangeSets';
import { ChangeSetNameFields } from '../components/ChangeSetNameFields';
import { useStagedChangeSetFeedback } from '../components/useStagedChangeSetFeedback';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list: ItemListDTO;
}

/**
 * Stage an item list's products as a new change set for review. The list's
 * keys are resolved server-side (`stageFromItemList`) — nothing large leaves
 * the client — and success navigates straight to the new review.
 */
export function CreateReviewFromListDialog(props: Props) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Mount the form only while open so its fields reset on every open. */}
        {props.open && <CreateReviewFromListForm {...props} />}
      </DialogContent>
    </Dialog>
  );
}

function CreateReviewFromListForm({ onOpenChange, list }: Props) {
  const gt = useGT();
  const stageFromList = useStageFromItemList();
  const feedback = useStagedChangeSetFeedback({
    landOn: 'review',
    onDone: () => onOpenChange(false),
  });
  // Seeded from the list so the common case is one click.
  const [name, setName] = useState(list.name);
  const [description, setDescription] = useState('');

  const canSubmit = name.trim().length > 0 && !stageFromList.isPending;

  const submit = () => {
    stageFromList.mutate(
      {
        itemListId: list.id,
        name: name.trim(),
        description: description.trim() || undefined,
      },
      feedback,
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="create review from item list dialog title">
            Create review from list
          </T>
        </DialogTitle>
        <DialogDescription>
          {gt(
            'The {count} products of "{name}" will be staged as a new change set for review.',
            {
              context:
                'create review from item list dialog subtitle showing the product count',
              count: list.itemCount.toLocaleString(),
              name: list.name,
            },
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4 py-2">
        <ChangeSetNameFields
          idPrefix="review-from-list"
          name={name}
          onNameChange={setName}
          description={description}
          onDescriptionChange={setDescription}
          disabled={stageFromList.isPending}
        />
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={stageFromList.isPending}
        >
          <T context="cancel button in the create review from list dialog">
            Cancel
          </T>
        </Button>
        <Button onClick={() => canSubmit && submit()} disabled={!canSubmit}>
          {stageFromList.isPending ? (
            <T context="submit button label while staging is in progress">
              Staging…
            </T>
          ) : (
            <T context="submit button label in the create review from list dialog">
              Create review
            </T>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
